import { Button } from "@package/subframe/components/Button";
import { Checkbox } from "@package/subframe/components/Checkbox";
import { IconButton } from "@package/subframe/components/IconButton";
import { Select } from "@package/subframe/components/Select";
import { Switch } from "@package/subframe/components/Switch";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherArrowLeft } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useState } from "react";
import { toast } from "sonner";

import { FollowUpActionSuccessDialog } from "@/components/follow-up-actions/FollowUpActionSuccessDialog";
import { ROLES } from "@/components/schedule/ScheduleTab";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getGetEmergencyLightCheckQueryControllerGetQueryKey } from "@/generated/emergency-light-checks/emergency-light-checks";
import { getGetEventRecordQueryControllerGetQueryKey } from "@/generated/event-records/event-records";
import { getGetFireExtinguisherCheckQueryControllerGetQueryKey } from "@/generated/fire-extinguisher-checks/fire-extinguisher-checks";
import { useCreateActionCommandControllerCreate } from "@/generated/follow-up-actions/follow-up-actions";
import { useListNurseryMembersQueryControllerListMembers } from "@/generated/members/members";
import {
  type CreateActionDtoAssigneeRole,
  CreateActionSourceDtoType,
} from "@/generated/model";
import { invalidateFollowUpActionQueries } from "@/lib/follow-up-actions/invalidate-follow-up-action-queries";
import { getFollowUpReturnDestination } from "@/lib/follow-up-actions/return-destination";

type SearchParams = {
  // Common
  nurseryId: string;
  // QUESTIONNAIRE_ANSWER source (legacy entry from checklist)
  scheduledQuestionnaireId: string;
  answerId: string;
  questionText: string;
  checklistName: string;
  // OUT_OF_RANGE_INCIDENT source (NAU-219 entry from incident detail page)
  outOfRangeIncidentId?: string;
  coldStorageName?: string;
  // FIRE_EXTINGUISHER_CHECK_ITEM source (NAU-242 entry from check detail page)
  fireExtinguisherCheckId?: string;
  fireExtinguisherCheckItemId?: string;
  extinguisherName?: string;
  emergencyLightCheckId?: string;
  emergencyLightCheckItemId?: string;
  emergencyLightName?: string;
  // EVENT_RECORD source (NAU-221 entry from a fire drill record detail page)
  eventRecordId?: string;
  eventRecordName?: string;
  // LOG_ENTRY source (NAU-241 entry from the log history)
  logEntryId?: string;
  logEntryContext?: string;
  // Set by "Submit and Raise Follow-up": the page behind this one is the
  // submitted form, not the record, so "back" must not be used to return.
  fromSubmission?: boolean;
};

export const Route = createFileRoute(
  "/_authenticated/follow-up-actions/create",
)({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    nurseryId: (search.nurseryId as string) || "",
    scheduledQuestionnaireId: (search.scheduledQuestionnaireId as string) || "",
    answerId: (search.answerId as string) || "",
    questionText: (search.questionText as string) || "",
    checklistName: (search.checklistName as string) || "",
    outOfRangeIncidentId:
      typeof search.outOfRangeIncidentId === "string"
        ? search.outOfRangeIncidentId
        : undefined,
    coldStorageName:
      typeof search.coldStorageName === "string"
        ? search.coldStorageName
        : undefined,
    fireExtinguisherCheckId:
      typeof search.fireExtinguisherCheckId === "string"
        ? search.fireExtinguisherCheckId
        : undefined,
    fireExtinguisherCheckItemId:
      typeof search.fireExtinguisherCheckItemId === "string"
        ? search.fireExtinguisherCheckItemId
        : undefined,
    emergencyLightCheckId:
      typeof search.emergencyLightCheckId === "string"
        ? search.emergencyLightCheckId
        : undefined,
    emergencyLightCheckItemId:
      typeof search.emergencyLightCheckItemId === "string"
        ? search.emergencyLightCheckItemId
        : undefined,
    emergencyLightName:
      typeof search.emergencyLightName === "string"
        ? search.emergencyLightName
        : undefined,
    extinguisherName:
      typeof search.extinguisherName === "string"
        ? search.extinguisherName
        : undefined,
    eventRecordId:
      typeof search.eventRecordId === "string"
        ? search.eventRecordId
        : undefined,
    eventRecordName:
      typeof search.eventRecordName === "string"
        ? search.eventRecordName
        : undefined,
    logEntryId:
      typeof search.logEntryId === "string" ? search.logEntryId : undefined,
    logEntryContext:
      typeof search.logEntryContext === "string"
        ? search.logEntryContext
        : undefined,
    fromSubmission: search.fromSubmission === true ? true : undefined,
  }),
  component: CreateFollowUpActionPage,
});

const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;
function isValidTime(value: string): boolean {
  return TIME_REGEX.test(value);
}

function TimeInput({
  value,
  onChange,
  placeholder,
  error,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  error: boolean;
  className?: string;
}) {
  const [touched, setTouched] = useState(false);
  const showError = touched && error;

  return (
    <div className={`relative ${className ?? "w-24 flex-none"}`}>
      <TextField className="h-auto w-full" size="large" error={showError}>
        <TextField.Input
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setTouched(true)}
        />
      </TextField>
      {showError && (
        <span className="absolute -bottom-5 left-0 text-[10px] font-caption text-error-700">
          HH:MM
        </span>
      )}
    </div>
  );
}

function CreateFollowUpActionPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    scheduledQuestionnaireId,
    answerId,
    nurseryId,
    questionText,
    outOfRangeIncidentId,
    coldStorageName,
    fireExtinguisherCheckId,
    fireExtinguisherCheckItemId,
    extinguisherName,
    emergencyLightCheckId,
    emergencyLightCheckItemId,
    emergencyLightName,
    eventRecordId,
    eventRecordName,
    logEntryId,
    logEntryContext,
    fromSubmission,
  } = Route.useSearch();

  const isFireExtinguisherSource =
    !!fireExtinguisherCheckId && !!fireExtinguisherCheckItemId;
  const isEmergencyLightSource =
    !!emergencyLightCheckId && !!emergencyLightCheckItemId;
  const isIncidentSource = !!outOfRangeIncidentId;
  const isEventRecordSource = !!eventRecordId;
  const isLogEntrySource = !!logEntryId;
  const subtitle = isLogEntrySource
    ? logEntryContext
      ? `For ${logEntryContext}`
      : "For a flagged log entry"
    : isEmergencyLightSource
      ? emergencyLightName
        ? `For ${emergencyLightName}`
        : "For a failed emergency light check"
      : isFireExtinguisherSource
        ? extinguisherName
          ? `For ${extinguisherName}`
          : "For a failed fire extinguisher check"
        : isIncidentSource
          ? coldStorageName
            ? `For ${coldStorageName}`
            : "For an open fridge incident"
          : isEventRecordSource
            ? eventRecordName
              ? `For ${eventRecordName}`
              : "For a fire drill record"
            : questionText;

  const [description, setDescription] = useState("");
  const [assigneeType, setAssigneeType] = useState<"role" | "team_members">(
    "role",
  );
  const [assigneeUserId, setAssigneeUserId] = useState<string>("");
  const [assigneeRole, setAssigneeRole] = useState<string>("");
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [dueTime, setDueTime] = useState("");
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);

  const createAction = useCreateActionCommandControllerCreate();

  // Fetch nursery members for the assignee dropdown
  const { data: membersData, isLoading: isMembersLoading } =
    useListNurseryMembersQueryControllerListMembers(
      { nurseryId, limit: 100 },
      { query: { enabled: !!nurseryId } },
    );

  const members = membersData?.data?.data || [];

  const returnDestination = getFollowUpReturnDestination({
    scheduledQuestionnaireId,
    outOfRangeIncidentId,
    fireExtinguisherCheckId,
    fireExtinguisherCheckItemId,
    emergencyLightCheckId,
    emergencyLightCheckItemId,
    eventRecordId,
    logEntryId,
  });

  const navigateToSource = () => {
    switch (returnDestination.kind) {
      case "logEntry":
        navigate({
          to: "/dashboard",
          search: { view: "today", tab: "logs" },
          replace: true,
        });
        return;
      case "emergencyLightCheck":
        navigate({
          to: "/emergency-light-checks/$checkId",
          params: { checkId: returnDestination.emergencyLightCheckId },
          replace: true,
        });
        return;
      case "fireExtinguisherCheck":
        navigate({
          to: "/fire-extinguisher-checks/$checkId",
          params: { checkId: returnDestination.fireExtinguisherCheckId },
          replace: true,
        });
        return;
      case "incident":
        navigate({
          to: "/incidents/$incidentId",
          params: { incidentId: returnDestination.outOfRangeIncidentId },
          replace: true,
        });
        return;
      case "record":
        navigate({
          to: "/event-records/$eventRecordId",
          params: { eventRecordId: returnDestination.eventRecordId },
          replace: true,
        });
        return;
      case "checklist":
        navigate({
          to: "/checklists/$id",
          params: { id: returnDestination.scheduledQuestionnaireId },
          replace: true,
        });
        return;
      case "none":
        return;
    }
  };

  const handleBack = () => {
    // "Submit and Raise Follow-up" replaced the check form with this one, so
    // history.back() would land on the list the form was opened from.
    if (fromSubmission) {
      navigateToSource();
      return;
    }
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    if (isLogEntrySource) {
      navigate({ to: "/dashboard", search: { view: "today", tab: "logs" } });
      return;
    }
    if (isEmergencyLightSource) {
      navigate({
        to: "/emergency-light-checks/$checkId",
        params: { checkId: emergencyLightCheckId! },
      });
      return;
    }
    if (isFireExtinguisherSource) {
      navigate({
        to: "/fire-extinguisher-checks/$checkId",
        params: { checkId: fireExtinguisherCheckId! },
      });
      return;
    }
    if (isIncidentSource) {
      navigate({
        to: "/incidents/$incidentId",
        params: { incidentId: outOfRangeIncidentId! },
      });
      return;
    }
    if (isEventRecordSource) {
      navigate({
        to: "/event-records/$eventRecordId",
        params: { eventRecordId: eventRecordId! },
      });
      return;
    }
    navigate({
      to: "/checklists/$id",
      params: { id: scheduledQuestionnaireId },
    });
  };

  // Pop this form off the history stack rather than navigating to the source.
  // The source is already the entry underneath, so pushing left a submitted form
  // for Back to re-open, and replacing left two identical adjacent entries so the
  // first Back appeared to do nothing. The navigations below are the fallback for
  // when the form was opened directly, with no history to return to.
  const handleReturnToSource = () => {
    // Covering the last failed check auto-acknowledges the checklist server-side
    // (NAU-71), so the Failed checks tab and dashboard counts are now stale.
    // Refetching here rather than straight after saving gives that background
    // acknowledgement time to land; `refetchOnMount` is off globally, so the
    // refetch has to be forced rather than left to the next mount. Not awaited —
    // the refetch is already in flight before we navigate.
    if (scheduledQuestionnaireId) {
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey?.[0];
          return (
            typeof key === "string" &&
            (key.includes("/scheduled-questionnaires") ||
              key.includes("/dashboard"))
          );
        },
        refetchType: "all",
      });
    }

    if (!fromSubmission && window.history.length > 1) {
      window.history.back();
      return;
    }

    navigateToSource();
  };

  const isDueTimeValid = !dueTime || isValidTime(dueTime);

  const hasAssignee =
    (assigneeType === "role" && !!assigneeRole) ||
    (assigneeType === "team_members" && !!assigneeUserId);

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error("Please enter a description for the action");
      return;
    }

    if (!hasAssignee) {
      toast.error("Please assign this action to a role or team member");
      return;
    }

    if (dueTime && !isDueTimeValid) {
      toast.error("Please enter a valid time in HH:MM format");
      return;
    }

    try {
      const source = isLogEntrySource
        ? {
            type: CreateActionSourceDtoType.LOG_ENTRY,
            logEntryId,
          }
        : isEmergencyLightSource
          ? {
              type: CreateActionSourceDtoType.EMERGENCY_LIGHT_CHECK_ITEM,
              emergencyLightCheckId,
              emergencyLightCheckItemId,
            }
          : isFireExtinguisherSource
            ? {
                type: CreateActionSourceDtoType.FIRE_EXTINGUISHER_CHECK_ITEM,
                fireExtinguisherCheckId,
                fireExtinguisherCheckItemId,
              }
            : isEventRecordSource
              ? {
                  type: CreateActionSourceDtoType.EVENT_RECORD,
                  eventRecordId,
                }
              : isIncidentSource
                ? {
                    type: CreateActionSourceDtoType.OUT_OF_RANGE_INCIDENT,
                    outOfRangeIncidentId,
                  }
                : {
                    type: CreateActionSourceDtoType.QUESTIONNAIRE_ANSWER,
                    scheduledQuestionnaireId,
                    answerId,
                  };

      await createAction.mutateAsync({
        data: {
          source,
          nurseryId,
          description: description.trim(),
          ...(assigneeType === "team_members" && assigneeUserId
            ? { assigneeUserId }
            : {}),
          ...(assigneeType === "role" && assigneeRole
            ? { assigneeRole: assigneeRole as CreateActionDtoAssigneeRole }
            : {}),
          ...(dueDate ? { dueDate: format(dueDate, "yyyy-MM-dd") } : {}),
          ...(dueTime ? { dueTime } : {}),
          ...(requiresApproval ? { requiresApproval: true } : {}),
        },
      });

      // Invalidate follow-up actions queries so lists refresh
      await invalidateFollowUpActionQueries(queryClient);

      // The check detail reads its follow-ups from the check query (not matched
      // above); refetchType "all" forces it to refresh despite refetchOnMount=false.
      if (isEmergencyLightSource && emergencyLightCheckId) {
        await queryClient.invalidateQueries({
          queryKey: getGetEmergencyLightCheckQueryControllerGetQueryKey(
            emergencyLightCheckId,
          ),
          refetchType: "all",
        });
      }

      if (isFireExtinguisherSource && fireExtinguisherCheckId) {
        await queryClient.invalidateQueries({
          queryKey: getGetFireExtinguisherCheckQueryControllerGetQueryKey(
            fireExtinguisherCheckId,
          ),
          refetchType: "all",
        });
      }

      // The event record detail reads its follow-ups from its own detail query
      // (key is /event-records, not matched above), so invalidate it explicitly
      // to show the new task on return.
      if (isEventRecordSource && eventRecordId) {
        await queryClient.invalidateQueries({
          queryKey: getGetEventRecordQueryControllerGetQueryKey(eventRecordId),
          refetchType: "all",
        });
      }

      setIsSuccessOpen(true);
    } catch {
      toast.error("Failed to create follow-up action");
    }
  };

  const isSubmitting = createAction.isPending;

  // Validate required params by source type.
  const hasValidSource = isLogEntrySource
    ? !!logEntryId && !!nurseryId
    : isEmergencyLightSource
      ? !!emergencyLightCheckId && !!emergencyLightCheckItemId && !!nurseryId
      : isFireExtinguisherSource
        ? !!fireExtinguisherCheckId &&
          !!fireExtinguisherCheckItemId &&
          !!nurseryId
        : isIncidentSource
          ? !!outOfRangeIncidentId && !!nurseryId
          : isEventRecordSource
            ? !!eventRecordId && !!nurseryId
            : !!scheduledQuestionnaireId && !!answerId && !!nurseryId;
  if (!hasValidSource) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-body font-body text-subtext-color">
          Missing required information. Please go back and try again.
        </p>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-col items-start gap-1 pb-8">
        <div className="flex w-full flex-wrap items-center gap-6">
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
            <div className="flex w-full items-start gap-2">
              <IconButton
                aria-label="Back"
                icon={<FeatherArrowLeft />}
                onClick={handleBack}
              />
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                <span className="w-full whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
                  Create follow-up action
                </span>
                {subtitle && (
                  <span className="text-body font-body text-subtext-color line-clamp-1">
                    {subtitle}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex w-full max-w-2xl mx-auto flex-col items-start gap-6 pb-8">
        {/* Description */}
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-body-bold font-body-bold text-neutral-900">
            What needs to be done?
          </span>
          <TextArea className="h-auto w-full flex-none" variant="outline">
            <TextArea.Input
              placeholder="Describe the remediation action required..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[100px]"
            />
          </TextArea>
        </div>

        {/* Assign to */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-body-bold font-body-bold text-neutral-900">
            Assign to
          </span>

          {/* Assignment type toggle */}
          <div className="flex w-full items-center gap-2">
            <Button
              className="h-10 grow shrink-0 basis-0"
              variant={
                assigneeType === "role" ? "brand-primary" : "neutral-secondary"
              }
              size="large"
              onClick={() => {
                setAssigneeType("role");
                setAssigneeUserId("");
              }}
            >
              Role
            </Button>
            <Button
              className="h-10 grow shrink-0 basis-0"
              variant={
                assigneeType === "team_members"
                  ? "brand-primary"
                  : "neutral-secondary"
              }
              size="large"
              onClick={() => {
                setAssigneeType("team_members");
                setAssigneeRole("");
              }}
            >
              Team members
            </Button>
          </div>

          {/* Role selector */}
          {assigneeType === "role" && (
            <Select
              className="h-10 w-full"
              variant="outline"
              placeholder="Select role"
              size="large"
              value={assigneeRole}
              onValueChange={setAssigneeRole}
            >
              {ROLES.map((role) => (
                <Select.Item key={role.value} value={role.value} size="large">
                  {role.label}
                </Select.Item>
              ))}
            </Select>
          )}

          {/* Team members list */}
          {assigneeType === "team_members" && (
            <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-default-background p-3 max-h-[240px] overflow-y-auto">
              {isMembersLoading ? (
                <span className="text-body font-body text-subtext-color">
                  Loading team members...
                </span>
              ) : members.length === 0 ? (
                <span className="text-body font-body text-subtext-color">
                  No team members found
                </span>
              ) : (
                members.map((member) => {
                  const displayName =
                    [member.firstName, member.lastName]
                      .filter(Boolean)
                      .join(" ") || member.email;
                  const isSelected = assigneeUserId === member.userId;
                  return (
                    <div
                      key={member.userId}
                      className="flex w-full items-center gap-3 py-2 px-1 rounded-md hover:bg-neutral-50 cursor-pointer"
                      onClick={() => setAssigneeUserId(member.userId)}
                    >
                      <Checkbox checked={isSelected} />
                      <div className="flex flex-col items-start">
                        <span className="text-body-bold font-body-bold text-neutral-900">
                          {displayName}
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          {ROLES.find(
                            (r) => r.value === (member.role as string),
                          )?.label || member.role}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Due date and time */}
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-body-bold font-body-bold text-neutral-900">
            Due date
            <span className="text-caption font-caption text-subtext-color ml-1">
              (optional)
            </span>
          </span>
          <div className="flex w-full items-start gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex h-10 grow shrink-0 basis-0 items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background px-3 text-body font-body text-neutral-900 hover:border-neutral-400"
                >
                  <span>
                    {dueDate
                      ? format(dueDate, "EEEE, MMMM d, yyyy")
                      : "Select date"}
                  </span>
                  <svg
                    className="h-4 w-4 text-neutral-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={setDueDate}
                  disabled={(date) =>
                    date < new Date(new Date().setHours(0, 0, 0, 0))
                  }
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <TimeInput
              value={dueTime}
              onChange={setDueTime}
              placeholder="HH:MM"
              error={!!dueTime && !isDueTimeValid}
            />
          </div>
        </div>

        {/* Requires sign-off */}
        <div className="flex w-full items-center gap-3 py-1">
          <Switch
            checked={requiresApproval}
            onCheckedChange={(checked) => setRequiresApproval(checked === true)}
          />
          <div className="flex flex-col items-start">
            <span className="text-body-bold font-body-bold text-neutral-900">
              Requires manager sign-off
            </span>
            <span className="text-caption font-caption text-subtext-color">
              A manager must approve before this action is marked as complete
            </span>
          </div>
        </div>

        {/* Info */}
        <div className="flex w-full items-start gap-2 rounded-md bg-neutral-50 px-4 py-3">
          <span className="text-caption font-caption text-subtext-color">
            {isLogEntrySource
              ? "This action will be linked to the log entry. The original entry will not be changed."
              : isIncidentSource
                ? "This action will be linked to the open fridge incident. The incident itself stays open until a manager signs it off."
                : isFireExtinguisherSource
                  ? "This action will be linked to the fire extinguisher check and failed extinguisher. The original check result will not be changed."
                  : isEventRecordSource
                    ? "This action will be linked to the fire drill record as a corrective action. The submitted record itself will not be changed."
                    : "This action will be linked to the checklist and failed item. The original checklist result will not be changed."}
          </span>
        </div>

        {/* Footer Actions */}
        <div className="flex w-full items-center justify-end gap-2 border-t border-solid border-secondary-beige-200 pt-6">
          <Button variant="neutral-primary" size="large" onClick={handleBack}>
            Cancel
          </Button>
          <Button
            size="large"
            onClick={handleSubmit}
            disabled={isSubmitting || !description.trim() || !hasAssignee}
          >
            {isSubmitting ? "Creating..." : "Create action"}
          </Button>
        </div>
      </div>

      <FollowUpActionSuccessDialog
        open={isSuccessOpen}
        sourceLabel={
          returnDestination.kind === "none" ? null : returnDestination.label
        }
        onReturnToSource={handleReturnToSource}
        onReturnToDashboard={() => navigate({ to: "/dashboard" })}
      />
    </div>
  );
}
